



import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Badge, Button, Spinner, Row, Col, Modal, Container } from 'react-bootstrap';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaBoxes, FaCalendarAlt, FaEdit, FaTrash, FaUser, FaImage } from 'react-icons/fa';
import { FiDownload, FiZoomIn } from "react-icons/fi";
import Select from 'react-select';

const ProductionOrderView = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();
  const [imageBlobs, setImageBlobs] = useState({});

  const [images, setImages] = useState([]);
  const [fileType, setFileType] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [modalShow, setModalShow] = useState(false);

  const [productionOrder, setProductionOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showInwardModal, setShowInwardModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [inwarddate, setInwardDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
          headers: authHeader()
        });
        const options = response.data.map(w => ({
          value: w.id,
          label: w.warehousename
        }));
        setWarehouses(options);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    };

    if (showInwardModal) {
      fetchWarehouses();
    }
  }, [showInwardModal]);

  const handleInwardSubmit = async () => {
    if (!selectedWarehouse) {
      return Swal.fire('Warning', 'Please select a warehouse', 'warning');
    }

    if (!inwarddate) {
      return Swal.fire('Warning', 'Please select an inward date', 'warning');
    }

    try {
      const url = `${BASE_URL}productionorder/update/warehouse/${id}/${selectedWarehouse.value}/${inwarddate}`;

      await axios.put(url, {}, {
        headers: authHeader(),
      });

      Swal.fire('Success', 'Inward details updated successfully!', 'success');
      setShowInwardModal(false);
    } catch (error) {
      console.error('Error updating inward details:', error);
      Swal.fire('Error', 'Failed to update inward details', 'error');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Open': return 'primary';
      case 'Ongoing': return 'warning';
      case 'On Hold': return 'danger';
      case 'Complete': return 'success';
      case 'Cancelled': return 'secondary';
      case 'Closed': return 'dark';
      default: return 'info';
    }
  };

  useEffect(() => {
    const fetchProductionOrderData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}productionorder/${id}`, {
          headers: authHeader()
        });
        setProductionOrder(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching production order data', error);
        setError('Failed to fetch production order data');
        setLoading(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch production order data',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate("/Porderindex/1");
        });
      }
    };

    const fetchImages = async () => {
      try {
        const res = await axios.get(`${BASE_URL}productionorder/getimages/${id}`, {
          headers: authHeader()
        });
        setImages(res.data || []);
      } catch (err) {
        console.error("Error fetching images", err);
      }
    };

    fetchProductionOrderData();
    fetchImages();
  }, [id, navigate]);

  useEffect(() => {
    const fetchAllImageBlobs = async () => {
      const blobs = {};
      for (const img of images) {
        try {
          const response = await axios.get(`${BASE_URL}productionorder/singleimages/view/${img.id}`, {
            headers: authHeader(),
            responseType: 'blob',
          });
          const url = URL.createObjectURL(response.data);
          blobs[img.id] = url;
        } catch (err) {
          console.error("Error fetching image blob for id:", img.id, err);
        }
      }
      setImageBlobs(blobs);
    };

    if (images.length > 0) {
      fetchAllImageBlobs();
    }
  }, [images]);

  const fetchAndOpenFileById = async (imageId) => {
    try {
      const response = await axios.get(`${BASE_URL}productionorder/singleimages/view/${imageId}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      setFileType(contentType.startsWith('image') ? 'image' : 'pdf');
      setFileUrl(url);
      setModalShow(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to open file.',
      });
    }
  };

  const downloadFileById = async (imageId, filename) => {
    try {
      const response = await axios.get(`${BASE_URL}productionorder/singleimages/view/${imageId}`, {
        headers: authHeader(),
        responseType: 'blob',
      });

      const fileExtension = filename.split('.').pop().toLowerCase();
      const contentTypeMap = {
        'pdf': 'application/pdf',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
      };
      const contentType = contentTypeMap[fileExtension] || 'application/octet-stream';

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Could not download file.',
        confirmButtonColor: "#0d6efd",
      });
    }
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setFileUrl(null);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}productionorder/${id}`, {
          headers: authHeader(),
        });
        navigate(`/Porderindex/${pointindex}`);
        Swal.fire('Deleted!', 'Production order deleted successfully.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data || 'An error occurred', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error || !productionOrder) {
    return <div className="alert alert-danger text-center mt-4">{error || "Production order not found"}</div>;
  }

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">PRODUCTION ORDER - VIEW</h5>
            <div>
              <Link to={`/PorderUpdate/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FaEdit className="me-1" />
                Update
              </Link>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(id)}>
                <FaTrash className="me-1" />
                Delete
              </Button>
              {productionOrder.status === 'Closed' && (
                <Button variant="outline-success" size="sm" className="me-2" onClick={() => setShowInwardModal(true)}>
                  <FaBoxes className="me-1" />
                  Inward
                </Button>
              )}
              <Link to={`/Porderindex/${pointindex}`} className="btn btn-secondary btn-sm">
                <FaArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Order Number</small>
                <div className="fw-bold">{productionOrder.productionordernumber || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Type</small>
                <div className="fw-bold">{productionOrder.type || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Job Number</small>
                <div className="fw-bold">{productionOrder.jobnumber || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg={getStatusBadge(productionOrder.status)} className="px-2 py-1">
                    {productionOrder.status || '-'}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Basic Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Basic Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Order Number</small>
              <div className="fw-bold">{productionOrder.productionordernumber || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Part Number</small>
              <div className="fw-bold">{productionOrder.partno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Order Date</small>
              <div className="fw-bold">
                <FaCalendarAlt className="me-2 text-muted" size={12} />
                {productionOrder.productionorderdate ? new Date(productionOrder.productionorderdate).toLocaleDateString('en-IN') : '-'}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Customer & Operator Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Customer & Operator Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={6}>
              <small className="text-muted">Customer Name</small>
              <div className="fw-bold">
                <FaUser className="me-2 text-muted" size={12} />
                {productionOrder.customer?.companyname || '-'}
              </div>
            </Col>
            <Col md={6}>
              <small className="text-muted">Operator(s)</small>
              <div className="fw-bold">
                <FaUser className="me-2 text-muted" size={12} />
                {productionOrder.operator?.length > 0
                  ? productionOrder.operator.map(op => `${op.firstname} ${op.lastname || ''}`).join(', ')
                  : '-'}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Schedule Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Schedule Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={3}>
              <small className="text-muted">Inhouse Start Date</small>
              <div className="fw-bold">
                <FaCalendarAlt className="me-2 text-muted" size={12} />
                {productionOrder.inhousestartdate ? new Date(productionOrder.inhousestartdate).toLocaleDateString('en-IN') : '-'}
              </div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Inhouse End Date</small>
              <div className="fw-bold">
                <FaCalendarAlt className="me-2 text-muted" size={12} />
                {productionOrder.inhouseenddate ? new Date(productionOrder.inhouseenddate).toLocaleDateString('en-IN') : '-'}
              </div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Outward Jobwork Date</small>
              <div className="fw-bold">
                <FaCalendarAlt className="me-2 text-muted" size={12} />
                {productionOrder.outwardjobworkdate ? new Date(productionOrder.outwardjobworkdate).toLocaleDateString('en-IN') : '-'}
              </div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Inward Jobwork Date</small>
              <div className="fw-bold">
                <FaCalendarAlt className="me-2 text-muted" size={12} />
                {productionOrder.inwardjobworkdate ? new Date(productionOrder.inwardjobworkdate).toLocaleDateString('en-IN') : '-'}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Order Items */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">
            <FaBoxes className="me-2" size={14} />
            Order Items
          </h6>
        </div>
        <div className="p-0">
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="fw-bold py-2">#</th>
                  <th className="fw-bold py-2">Product Name</th>
                  <th className="fw-bold py-2 text-center">Quantity</th>
                  <th className="fw-bold py-2 text-center">UOM</th>
                  <th className="fw-bold py-2">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {productionOrder.popumpitems?.length > 0 ? (
                  productionOrder.popumpitems.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold">{item.bomproductname || '-'}</td>
                      <td className="text-center">{item.quantity || '0'}</td>
                      <td className="text-center">{item.uom || '-'}</td>
                      <td>{item.ramarks || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-3">No items found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Images Section */}
      {images.length > 0 && (
        <div className="shadow-sm border rounded bg-white mb-3">
          <div className="bg-light py-2 px-3 border-bottom">
            <h6 className="mb-0 fw-bold">
              <FaImage className="me-2" size={14} />
              Images / Documents
            </h6>
          </div>
          <div className="p-3">
            <Row className="g-3">
              {images.map((img) => (
                <Col md={4} xl={3} key={img.id}>
                  <div className="border rounded-3 p-3 text-center shadow-sm h-100 bg-white">
                    <div className="mb-3">
                      {imageBlobs[img.id] ? (
                        <img
                          src={imageBlobs[img.id]}
                          alt={img.filename}
                          style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '150px', backgroundColor: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FaImage size={40} className="text-muted" />
                        </div>
                      )}
                    </div>
                    <div className="fw-semibold small text-truncate mb-2">{img.filename}</div>
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => fetchAndOpenFileById(img.id)}
                        title="View"
                      >
                        <FiZoomIn size={12} />
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => downloadFileById(img.id, img.filename)}
                        title="Download"
                      >
                        <FiDownload size={12} />
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/PorderUpdate/${id}/${pointindex}`} className="btn btn-primary btn-sm me-2">
          <FaEdit className="me-1" />
          Update Order
        </Link>
        <Link to={`/Porderindex/${pointindex}`} className="btn btn-secondary btn-sm">
          <FaArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>

      {/* File Preview Modal */}
      <Modal show={modalShow} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 bg-light">
          {fileType === 'image' && (
            <img src={fileUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          )}
          {fileType === 'pdf' && (
            <iframe
              src={fileUrl}
              title="PDF Preview"
              width="100%"
              height="600px"
              style={{ border: 'none' }}
            ></iframe>
          )}
          {!fileType && <p className="p-4">No preview available</p>}
        </Modal.Body>
      </Modal>

      {/* Inward Modal */}
      <Modal show={showInwardModal} onHide={() => setShowInwardModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Inward Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Select Warehouse</label>
            <Select
              options={warehouses}
              value={selectedWarehouse}
              onChange={setSelectedWarehouse}
              placeholder="Choose warehouse..."
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Inward Date</label>
            <input
              type="date"
              className="form-control"
              value={inwarddate}
              onChange={(e) => setInwardDate(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowInwardModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleInwardSubmit}>Submit</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductionOrderView;